import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { scaleImage } from "../../utils/scaleImage";
import "./AlwaysInMotion.scss";
import { SkeletonImage } from "../@shared/SkeletonImage";

interface Teaser {
  id: string;
  name: string;
  fields: {
    teaserTitle: { value: string };
    teaserLink: {
      value: {
        text: string;
        href: string;
      };
    };
    downloadLink: {
      value: {
        text: string;
        href: string;
      };
    };
    teaserDescription: { value: string };
    teaserImage: {
      value: {
        src: string;
        alt: string;
      };
    };
    date: { value: string };
  };
  teaserType: string;
  hashTag?: string;
  itemUrl?: string;
  pageLink?: string;
}

interface AlwaysInMotionFields {
  Title: { value: string };
  Subtitle: { value: string };
  PressReleasesLabel: { value: string };
  NewsLabel: { value: string };
  pressReleasesFilterUrl: string;
  newsAndEventsFilterUrl: string;
}

interface AlwaysInMotionProps {
  fields: AlwaysInMotionFields;
}

const AlwaysInMotion = ({ fields }: AlwaysInMotionProps) => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<"press" | "news">("press");
  const [pressTeasers, setPressTeasers] = useState<Teaser[]>([]);
  const [newsTeasers, setNewsTeasers] = useState<Teaser[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [pressResponse, newsResponse] = await Promise.all([
        axios.get(fields.pressReleasesFilterUrl),
        axios.get(fields.newsAndEventsFilterUrl),
      ]);

      setPressTeasers(pressResponse.data.teasers.slice(0, 4));
      setNewsTeasers([
        ...newsResponse.data.teasers.slice(0, 4),
        //...eventsResponse.data.teasers.slice(0, 2),
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const teasersToDisplay = activeTab === "press" ? pressTeasers : newsTeasers;

  return (
    <div className="AlwaysInMotion" aria-label="Always In Motion Section">
      <div className="container-sm py-5">
        <div className="d-flex flex-wrap flex-row">
          <div className="col-12 col-md-12 col-lg-6 col-xl-7">
            <h1
              className="AlwaysInMotion__title"
              dangerouslySetInnerHTML={{ __html: fields.Title.value }}
              aria-label="Title"
            />
            <div
              className="AlwaysInMotion__subtitle"
              dangerouslySetInnerHTML={{ __html: fields.Subtitle.value }}
              aria-label="Subtitle"
            />
            <div
              className="d-flex AlwaysInMotion__tab_container"
              role="tablist"
            >
              <h4
                onClick={() => setActiveTab("press")}
                className={`text-center cursor-pointer AlwaysInMotion__tab${
                  activeTab === "press" ? "_active" : ""
                }`}
                role="tab"
                aria-selected={activeTab === "press"}
                tabIndex={0}
                aria-label="Press Releases Tab"
              >
                <em>{fields.PressReleasesLabel.value}</em>
              </h4>
              <h4
                onClick={() => setActiveTab("news")}
                className={`text-center cursor-pointer AlwaysInMotion__tab${
                  activeTab === "news" ? "_active" : ""
                }`}
                role="tab"
                aria-selected={activeTab === "news"}
                tabIndex={0}
                aria-label="News Tab"
              >
                <em>{fields.NewsLabel.value}</em>
              </h4>
            </div>
          </div>
          <div className="col-12 col-md-12 col-lg-6 col-xl-5 AlwaysInMotion__content mt-4">
            {loading ? (
              <div className="text-center mt-5" aria-live="polite">
                <div
                  className="spinner-border"
                  role="status"
                  aria-label="Loading"
                >
                  {/* <span className="visually-hidden">Loading...</span> */}
                </div>
              </div>
            ) : (
              <div className="row">
                {teasersToDisplay.map((teaser, index) => (
                  <AlwaysInMotionCard
                    key={`aim-card-${index}`}
                    index={index}
                    teaser={teaser}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AlwaysInMotionCard: FC<{ index: number; teaser: Teaser }> = memo(
  ({ index, teaser }) => {
    const { t } = useTranslation();

    const [height, setHeight] = useState(158);
    const cardRef = useRef<HTMLDivElement>(null);

    console.log(teaser);

    const calcMinHeight = useCallback(() => {
      setHeight((cardRef.current.clientWidth / 3) * 2 + 124);
    }, [cardRef]);

    useEffect(() => {
      if (!cardRef.current || typeof window === "undefined") return;

      calcMinHeight();
      window.addEventListener("resize", calcMinHeight);
      return () => window.removeEventListener("resize", calcMinHeight);
    }, [cardRef]);

    return (
      <div
        className={`col-6 col-lg-6 mb-5 ${
          index % 2 === 1 ? "AlwaysInMotion__card--offset" : ""
        }`}
        aria-label={`Teaser ${index + 1}`}
      >
        <div
          ref={cardRef}
          className="AlwaysInMotion__card"
          style={{
            height: height,
          }}
        >
          <a
            href={teaser.fields?.teaserLink?.value?.href ?? teaser?.itemUrl}
            aria-label={teaser.fields.teaserTitle.value}
          >
            <SkeletonImage
              src={scaleImage(
                teaser.fields.teaserImage.value.src?.replace(/\?.*$/, ""),
                480,
                1
              )}
              alt={teaser.fields.teaserImage.value.alt}
              className="img-fluid"
            />
            <div className="AlwaysInMotion__cardContent mt-2">
              {teaser?.hashTag && (
                <p className="AlwaysInMotion__cardTag">
                  <em>{teaser.hashTag}</em>
                </p>
              )}
              <h5 className="AlwaysInMotion__cardTitle">
                {teaser.fields.teaserTitle.value}
              </h5>
              {teaser.itemUrl && (
                <div className="AlwaysInMotion__cta">
                  <a
                    className="ga4-click-ui-press-news btn btn-outline-light"
                    href={teaser.itemUrl}
                  >
                    {t("ReadMore")}
                  </a>
                </div>
              )}
              {(teaser.fields?.teaserLink || teaser.fields?.downloadLink) && (
                <div className="AlwaysInMotion__cta">
                  <a
                    className="ga4-click-ui-press-news btn btn-outline-light"
                    href={
                      teaser.fields?.teaserLink?.value?.href ??
                      teaser.fields?.downloadLink?.value?.href
                    }
                  >
                    {t("ReadMore")}
                  </a>
                </div>
              )}
            </div>
          </a>
        </div>
      </div>
    );
  }
);

export default AlwaysInMotion;
